import { useMemo } from 'react';

const STATUS_META = {
  new:      { label: 'New',      pill: '🆕', color: '#0C447C', bg: '#E6F1FB' },
  hot:      { label: 'Hot',      pill: '🔥', color: '#A32D2D', bg: '#FCEBEB' },
  warm:     { label: 'Warm',     pill: '☀️', color: '#633806', bg: '#FAEEDA' },
  cold:     { label: 'Cold',     pill: '❄️', color: '#27500A', bg: '#EAF3DE' },
  followup: { label: 'Followup', pill: '⏰', color: '#854F0B', bg: '#FAEEDA' },
};

const timeAgo = (ts) => {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

function BucketCard({ title, color, leads, onOpen, emptyMsg }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${color}22`, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <div style={{ background: `linear-gradient(135deg, ${color}, ${color}dd)`, padding: '13px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>{title}</div>
        <div style={{ background: 'rgba(255,255,255,0.3)', color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>{leads.length}</div>
      </div>
      {leads.length === 0 ? (
        <div style={{ padding: '36px 16px', textAlign: 'center', color: '#aaa', fontSize: 13 }}>{emptyMsg}</div>
      ) : (
        leads.map(l => {
          const sm = STATUS_META[l.status] || STATUS_META.new;
          return (
            <div key={l.id} onClick={() => onOpen(l)} style={{ padding: '14px 18px', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f9f9f9'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>{l.name || l.phone}</div>
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>📱 {l.phone}</div>
                  {l.product_tag && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>📦 {l.product_tag}</div>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <span style={{ background: sm.bg, color: sm.color, padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700 }}>{sm.pill} {sm.label}</span>
                  <span style={{ fontSize: 10, color: '#aaa' }}>{timeAgo(l.created_at)}</span>
                </div>
              </div>
              {l.follow_up_date && (
                <div style={{ fontSize: 11, color: '#854F0B', marginTop: 6, background: '#FFF8E7', padding: '3px 8px', borderRadius: 6, display: 'inline-block' }}>
                  📅 Follow-up: {l.follow_up_date.slice(0,10)}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

export function AgentDailyView({ leads, onOpenTimeline }) {
  const today = new Date().toDateString();

  const buckets = useMemo(() => {
    const newLeads = leads.filter(l => l.status === 'new' && !['converted','lost'].includes(l.status));
    const reEngaged = leads.filter(l => {
      if (['converted','lost','new'].includes(l.status)) return false;
      if (!l.last_customer_reply_at) return false;
      return !l.last_agent_reply_at || new Date(l.last_customer_reply_at) > new Date(l.last_agent_reply_at);
    });
    const todayFollowup = leads.filter(l => {
      if (['converted','lost'].includes(l.status)) return false;
      if (!l.follow_up_date) return false;
      return new Date(l.follow_up_date).toDateString() === today;
    });
    const missedFollowup = leads.filter(l => {
      if (['converted','lost'].includes(l.status)) return false;
      if (!l.follow_up_date) return false;
      const fup = new Date(l.follow_up_date);
      return fup < new Date() && fup.toDateString() !== today;
    });
    return { newLeads, reEngaged, todayFollowup, missedFollowup };
  }, [leads, today]);

  const totalPending = buckets.newLeads.length + buckets.reEngaged.length + buckets.todayFollowup.length + buckets.missedFollowup.length;

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 19, fontWeight: 800, color: '#1a1a2e', letterSpacing: '-0.01em' }}>📋 My Daily Work View</div>
        <div style={{ fontSize: 12.5, color: '#8a8a8a', marginTop: 5 }}>
          {totalPending} leads to clear today · {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 18 }}>
        <BucketCard
          title="🆕 New Leads"
          color="#0C447C"
          leads={buckets.newLeads}
          onOpen={onOpenTimeline}
          emptyMsg="No new leads 🎉"
        />
        <BucketCard
          title="💬 Re-engaged"
          color="#1565c0"
          leads={buckets.reEngaged}
          onOpen={onOpenTimeline}
          emptyMsg="No re-engaged leads"
        />
        <BucketCard
          title="📅 Today's Follow-ups"
          color="#854F0B"
          leads={buckets.todayFollowup}
          onOpen={onOpenTimeline}
          emptyMsg="No follow-ups today 🎉"
        />
        <BucketCard
          title="⚠️ Missed Follow-ups"
          color="#c62828"
          leads={buckets.missedFollowup}
          onOpen={onOpenTimeline}
          emptyMsg="No missed follow-ups 🎉"
        />
      </div>
    </div>
  );
}
